import { useEffect, useState } from 'react';
import type { PictureRect } from '@/types/video-player';
import {
    computeObjectFitContainLayout,
    getVideoElementFromRef,
} from '@/utils/video-player';
import type { VideoPlayerEventEmitter } from '../VideoPlayerEventEmitter';

interface UseVideoPictureRectParams {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    emitter: VideoPlayerEventEmitter;
}

function isSameRect(a: PictureRect | null, b: PictureRect): boolean {
    return (
        a !== null &&
        a.width === b.width &&
        a.height === b.height &&
        a.offsetX === b.offsetX &&
        a.offsetY === b.offsetY &&
        a.scaleX === b.scaleX &&
        a.scaleY === b.scaleY
    );
}

export function useVideoPictureRect({
    videoRef,
    emitter,
}: UseVideoPictureRectParams): PictureRect | null {
    const [pictureRect, setPictureRect] = useState<PictureRect | null>(null);

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        let observedElement: HTMLVideoElement | null = null;

        function detach() {
            resizeObserver?.disconnect();
            resizeObserver = null;
            observedElement?.removeEventListener('resize', measure);
            observedElement = null;
        }

        const measure = () => {
            const videoElement = getVideoElementFromRef(videoRef);
            if (!videoElement) return;

            if (observedElement !== videoElement) {
                detach();
                observedElement = videoElement;
                resizeObserver = new ResizeObserver(measure);
                resizeObserver.observe(videoElement);
                videoElement.addEventListener('resize', measure);
            }

            const videoWidth = videoElement.videoWidth;
            const videoHeight = videoElement.videoHeight;
            if (!videoWidth || !videoHeight) {
                setPictureRect(null);
                return;
            }

            const { renderedWidth, renderedHeight, offsetX, offsetY } =
                computeObjectFitContainLayout(videoElement);

            const next: PictureRect = {
                width: renderedWidth,
                height: renderedHeight,
                offsetX,
                offsetY,
                scaleX: renderedWidth / videoWidth,
                scaleY: renderedHeight / videoHeight,
            };

            setPictureRect((current) =>
                isSameRect(current, next) ? current : next,
            );
        };

        const unsubscribers = [
            emitter.on('loadedmetadata', measure),
            emitter.on('ready', measure),
            emitter.on('play', measure),
            emitter.on('sourcechange', measure),
        ];
        measure();

        return () => {
            detach();
            unsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [videoRef, emitter]);

    return pictureRect;
}
